import './TraderDetail.scss';
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import Toast from '../../components/Toast/Toast';
import { getApiErrorMessage } from '../../utils/apiErrors';

const MAX_MIN_BALANCE = 32767;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function TraderDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api_url = useSelector((state) => state.API_URL);

    const [trader, setTrader] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    // Editable fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [minBalance, setMinBalance] = useState(250);
    const [newPassword, setNewPassword] = useState('');

    // Payment settings
    const [paymentData, setPaymentData] = useState(null);
    const [feeValues, setFeeValues] = useState({});
    const [initialFeeValues, setInitialFeeValues] = useState({});
    const [savingFees, setSavingFees] = useState(false);

    const fetchTrader = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        if (!jwtToken) return;
        try {
            const response = await fetch(api_url + `teamlead/members/${id}/`, {
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
            });
            if (response.ok) {
                const data = await response.json();
                setTrader(data);
                setFirstName(data.first_name || '');
                setLastName(data.last_name || '');
                setEmail(data.email || '');
                setPhone(data.phone || '');
                setIsActive(data.is_active);
                setMinBalance(data.min_allowed_balance ?? 250);
            } else if (response.status === 404) {
                setToast({ type: 'error', message: 'Трейдер не найден' });
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [api_url, id]);

    const fetchPaymentSettings = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        if (!jwtToken) return;
        try {
            const response = await fetch(api_url + `teamlead/members/${id}/payment-settings/`, {
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
            });
            if (response.ok) {
                const data = await response.json();
                setPaymentData(data);
                // Инициализируем fee values из текущих настроек
                const fees = {};
                data.operator_settings.forEach(s => { fees[s.payment_settings_id] = s.fee; });
                setFeeValues(fees);
                setInitialFeeValues(fees);
            }
        } catch (e) { console.error(e); }
    }, [api_url, id]);

    useEffect(() => { fetchTrader(); fetchPaymentSettings(); }, [fetchTrader, fetchPaymentSettings]);

    const handleSave = async () => {
        const parsedMinBalance = Number(minBalance);
        if (!Number.isInteger(parsedMinBalance) || parsedMinBalance < 0 || parsedMinBalance > MAX_MIN_BALANCE) {
            setToast({
                type: 'error',
                message: `Мин. баланс должен быть целым числом от 0 до ${MAX_MIN_BALANCE}`,
            });
            return;
        }
        if (newPassword && newPassword.length < 6) {
            setToast({ type: 'error', message: 'Пароль должен быть не менее 6 символов' });
            return;
        }
        if (email && !EMAIL_PATTERN.test(email)) {
            setToast({ type: 'error', message: 'Укажите корректный Email' });
            return;
        }

        setSaving(true);
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const body = {
                first_name: firstName,
                last_name: lastName,
                email,
                phone,
                is_active: isActive,
                min_allowed_balance: parsedMinBalance,
            };
            if (newPassword) body.new_password = newPassword;

            const response = await fetch(api_url + `teamlead/members/${id}/`, {
                method: 'PUT',
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await response.json().catch(() => null);
            if (response.ok) {
                setToast({ type: 'success', message: data.message });
                setNewPassword('');
                fetchTrader();
            } else {
                setToast({ type: 'error', message: getApiErrorMessage(data, 'Не удалось сохранить трейдера') });
            }
        } catch (e) {
            setToast({ type: 'error', message: 'Ошибка сети' });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveFees = async () => {
        const settings = [];
        const changedFees = Object.entries(feeValues).filter(([psId, rawFee]) => (
            !Object.prototype.hasOwnProperty.call(initialFeeValues, psId)
            || Number(rawFee) !== Number(initialFeeValues[psId])
        ));

        if (changedFees.length === 0) {
            setToast({ type: 'info', message: 'Нет изменений для сохранения' });
            return;
        }

        for (const [psId, rawFee] of changedFees) {
            const paymentSetting = paymentData?.available_payment_settings.find(
                item => item.payment_settings_id === Number(psId)
            );
            const parsedFee = Number(rawFee);
            const maxFee = Number(paymentSetting?.max_operator_fee);

            if (!Number.isFinite(parsedFee) || !paymentSetting || parsedFee < 0 || parsedFee > maxFee) {
                const title = paymentSetting?.payment_settings_title || `ID ${psId}`;
                setToast({
                    type: 'error',
                    message: `Комиссия «${title}» должна быть числом от 0 до ${Number.isFinite(maxFee) ? maxFee : 0}%`,
                });
                return;
            }

            settings.push({ payment_settings_id: Number(psId), fee: parsedFee });
        }

        setSavingFees(true);
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + `teamlead/members/${id}/payment-settings/`, {
                method: 'PUT',
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings }),
            });
            const data = await response.json().catch(() => null);
            if (response.ok) {
                setToast({ type: 'success', message: data.message });
                fetchPaymentSettings();
            } else {
                setToast({ type: 'error', message: getApiErrorMessage(data, 'Не удалось сохранить настройки платежей') });
            }
        } catch (e) {
            setToast({ type: 'error', message: 'Ошибка сети' });
        } finally {
            setSavingFees(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleDateString('ru-RU') + ' ' + new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) return <div style={{ padding: '24px' }}><p>Загрузка...</p></div>;
    if (!trader) return <div style={{ padding: '24px' }}><p>Трейдер не найден</p></div>;

    return (
        <div className="TraderDetail">
            {toast && <Toast show type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

            <div className="TraderDetail__header">
                <button className="TraderDetail__back" onClick={() => navigate('/team')}>← Назад</button>
                <h2>{trader.username}</h2>
                {trader.is_frozen && (
                    <span className="TraderDetail__frozen-badge">{trader.freeze_kind || 'Заморожен'}</span>
                )}
            </div>

            {/* Info cards */}
            <div className="TraderDetail__info-cards">
                <div className="TraderDetail__info-card">
                    <span className="TraderDetail__info-label">Баланс</span>
                    <span className={`TraderDetail__info-value${trader.balance_usdt < 0 ? ' TraderDetail__info-value--neg' : ''}`}>
                        {trader.balance_usdt} $
                    </span>
                </div>
                <div className="TraderDetail__info-card">
                    <span className="TraderDetail__info-label">Реквизиты</span>
                    <span className="TraderDetail__info-value">{trader.active_creds}</span>
                </div>
                <div className="TraderDetail__info-card">
                    <span className="TraderDetail__info-label">Бот</span>
                    <span className="TraderDetail__info-value">{trader.has_chat ? '✓ Подключён' : '— Нет'}</span>
                </div>
                <div className="TraderDetail__info-card">
                    <span className="TraderDetail__info-label">Последний вход</span>
                    <span className="TraderDetail__info-value TraderDetail__info-value--small">{formatDate(trader.last_login)}</span>
                </div>
                <div className="TraderDetail__info-card">
                    <span className="TraderDetail__info-label">Активен до</span>
                    <span className="TraderDetail__info-value TraderDetail__info-value--small">{formatDate(trader.active_to)}</span>
                </div>
            </div>

            {/* Edit form */}
            <div className="TraderDetail__form-card">
                <h3>Редактирование</h3>

                <div className="TraderDetail__form-grid">
                    <div className="TraderDetail__field">
                        <label>Активен</label>
                        <div className="TraderDetail__toggle" onClick={() => setIsActive(!isActive)}>
                            <div className={`TraderDetail__toggle-track${isActive ? ' TraderDetail__toggle-track--on' : ''}`}>
                                <div className="TraderDetail__toggle-thumb" />
                            </div>
                            <span>{isActive ? 'Да' : 'Нет'}</span>
                        </div>
                    </div>

                    <div className="TraderDetail__field">
                        <label>Мин. баланс</label>
                        <input
                            type="number"
                            value={minBalance}
                            onChange={(e) => setMinBalance(e.target.value)}
                            min="0"
                            max={MAX_MIN_BALANCE}
                            step="1"
                        />
                    </div>

                    <div className="TraderDetail__field">
                        <label>Имя</label>
                        <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    </div>

                    <div className="TraderDetail__field">
                        <label>Фамилия</label>
                        <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </div>

                    <div className="TraderDetail__field">
                        <label>Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>

                    <div className="TraderDetail__field">
                        <label>Телефон</label>
                        <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>

                    <div className="TraderDetail__field TraderDetail__field--full">
                        <label>Новый пароль</label>
                        <input
                            type="text"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Оставьте пустым, чтобы не менять"
                        />
                    </div>
                </div>

                <button
                    className="TraderDetail__save"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
            </div>

            {/* Payment Settings */}
            {paymentData && (
                <div className="TraderDetail__form-card" style={{ marginTop: '24px' }}>
                    <h3>Настройки платежей (PayIn)</h3>

                    {paymentData.available_payment_settings.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Нет доступных способов оплаты</p>
                    ) : (
                        <>
                            <div className="TraderDetail__fees-table">
                                <div className="TraderDetail__fees-header">
                                    <span>Способ оплаты</span>
                                    <span>Валюта</span>
                                    <span>Ваша ставка</span>
                                    <span>Макс. для трейдера</span>
                                    <span>Ставка трейдера</span>
                                </div>
                                {paymentData.available_payment_settings.map(ps => (
                                    <div key={ps.payment_settings_id} className="TraderDetail__fees-row">
                                        <span>{ps.payment_settings_title}</span>
                                        <span className="TraderDetail__fees-currency">{ps.currency_code}</span>
                                        <span className="TraderDetail__fees-value">{ps.teamlead_fee}%</span>
                                        <span className="TraderDetail__fees-value">{ps.max_operator_fee}%</span>
                                        <span>
                                            <input
                                                type="number"
                                                className="TraderDetail__fees-input"
                                                value={feeValues[ps.payment_settings_id] ?? ''}
                                                onChange={(e) => setFeeValues(prev => ({
                                                    ...prev,
                                                    [ps.payment_settings_id]: e.target.value
                                                }))}
                                                min="0"
                                                max={ps.max_operator_fee}
                                                step="0.1"
                                                placeholder="—"
                                            />
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <button
                                className="TraderDetail__save"
                                onClick={handleSaveFees}
                                disabled={savingFees}
                                style={{ marginTop: '16px' }}
                            >
                                {savingFees ? 'Сохранение...' : 'Сохранить настройки'}
                            </button>
                        </>
                    )}

                    {/* PayOut settings (readonly) */}
                    {paymentData.payout_settings.length > 0 && (
                        <>
                            <h3 style={{ marginTop: '24px' }}>Настройки PayOut (только чтение)</h3>
                            <div className="TraderDetail__fees-table">
                                <div className="TraderDetail__fees-header">
                                    <span>Валюта</span>
                                    <span>Комиссия</span>
                                    <span>Приоритет</span>
                                </div>
                                {paymentData.payout_settings.map(ps => (
                                    <div key={ps.id} className="TraderDetail__fees-row">
                                        <span>{ps.currency_code}</span>
                                        <span>{ps.fee}%</span>
                                        <span>{ps.priority}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
